import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FaGraduationCap,
  FaBriefcase,
  FaTrophy,
  FaStethoscope,
  FaUsers,
  FaChartLine,
  FaComments,
  FaHandHoldingHeart,
  FaHeartbeat,
  FaClinicMedical,
  FaHospitalUser,
  FaAmbulance,
} from "react-icons/fa";
import styles from "./About.module.css";
import checkUpImage from ".././assets/about-us.jpg";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const About = () => {
  // Medical Center stats
  const centerStats = [
    {
      number: "15+",
      label: "Years of Excellence",
      icon: FaHeartbeat,
      color: "#2A7FBA",
    },
    {
      number: "10k+",
      label: "Happy Patients",
      icon: FaUsers,
      color: "#4CAF50",
    },
    {
      number: "24/7",
      label: "Emergency Care",
      icon: FaAmbulance,
      color: "#FF9800",
    },
    {
      number: "100%",
      label: "Patient Satisfaction",
      icon: FaComments,
      color: "#9C27B0",
    },
  ];

  // Doctor stats
  const doctorStats = [
    {
      number: "15+",
      label: "Years Experience",
      icon: FaStethoscope,
      color: "#2A7FBA",
    },
    {
      number: "10k+",
      label: "Patients Served",
      icon: FaUsers,
      color: "#4CAF50",
    },
    {
      number: "20%",
      label: "EHR Error Reduction",
      icon: FaChartLine,
      color: "#FF9800",
    },
    {
      number: "24/7",
      label: "Telemedicine Support",
      icon: FaComments,
      color: "#9C27B0",
    },
  ];

  const doctorCards = [
    {
      title: "Qualifications",
      icon: FaGraduationCap,
      color: "#2A7FBA",
      items: [
        "Master's in Organizational Development (USIU, 2012–2014)",
        "Bachelor's in Counseling Psychology (USIU, 2005–2011)",
        "Diploma in Clinical Medicine & Surgery (KMTC, 1997–2001)",
        "Certificate of Proficiency (Kenya College of Insurance, 2016)",
      ],
    },
    {
      title: "Experience Highlights",
      icon: FaBriefcase,
      color: "#4CAF50",
      items: [
        "Medical In-Charge – Associated Battery Manufacturers (2022–2025)",
        "Head of Medical Services – JARS Transporters Ltd (2016–2022)",
        "Health Services Coordinator – USIU (2004–2015)",
      ],
    },
    {
      title: "Key Achievements",
      icon: FaTrophy,
      color: "#FF9800",
      items: [
        "Implemented EHR systems, reducing documentation errors by 20%",
        "Developed wellness programs boosting employee engagement",
        "Partnerships with local universities for internships",
        "Led emergency response planning and training",
      ],
    },
  ];

  // Floating circles animation
  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      rotate: [0, 180, 360],
      transition: { duration: 6, repeat: Infinity, ease: "easeInOut" },
    },
  };

  return (
    <>
      <Helmet>
        <title>About Muigai Medical Center | Dr. Jack Kimeu</title>
        <meta
          name="description"
          content="Muigai Medical Center is a modern healthcare facility in Nairobi led by Dr. Jack Kimeu, offering comprehensive medical services with a patient-centered approach."
        />
      </Helmet>

      {/* Hero Banner – About the Medical Center */}
      <section
        style={{ backgroundImage: `url(${checkUpImage})` }}
        className={styles.heroBanner}
      >
        <div className={styles.heroOverlay}></div>
        <div className="container">
          <motion.div
            className={styles.heroContent}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <motion.div className={styles.sectionTag} variants={fadeInUp}>
              ABOUT US
            </motion.div>
            <motion.h1 variants={fadeInUp}>Muigai Medical Center</motion.h1>
            <motion.p className={styles.heroSubtitle} variants={fadeInUp}>
              Your Health, Our Priority – Compassionate Care for Every Patient
            </motion.p>
          </motion.div>
        </div>
        <div className={styles.heroBackground}>
          <motion.div
            className={styles.floatingCircle}
            variants={floatingVariants}
            animate="animate"
            style={{ top: "15%", left: "10%", width: "120px", height: "120px" }}
          />
          <motion.div
            className={styles.floatingCircle}
            variants={floatingVariants}
            animate="animate"
            style={{
              bottom: "20%",
              right: "15%",
              width: "80px",
              height: "80px",
              animationDelay: "2s",
            }}
          />
          <motion.div
            className={styles.floatingCircle}
            variants={floatingVariants}
            animate="animate"
            style={{
              top: "60%",
              left: "20%",
              width: "60px",
              height: "60px",
              animationDelay: "4s",
            }}
          />
        </div>
      </section>

      {/* Medical Center Introduction */}
      <section className={styles.centerIntro}>
        <div className="container">
          <div className={styles.centerIntroGrid}>
            <motion.div
              className={styles.centerIntroText}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <h2>Welcome to Muigai Medical Center</h2>
              <p>
                Muigai Medical Center is a modern healthcare facility dedicated
                to providing comprehensive, patient-centered care in Nairobi.
                Our state-of-the-art center offers a wide range of medical
                services under one roof, from general consultations to
                specialized treatments and telemedicine.
              </p>
              <p>
                We believe that quality healthcare should be accessible,
                compassionate, and personalized. Our team of experienced
                professionals, led by Jack Kimeu, works tirelessly to ensure
                every patient receives the attention and care they deserve. With
                a focus on preventive care and wellness, we empower our patients
                to take charge of their health.
              </p>
              <div className={styles.centerFeatures}>
                <div className={styles.feature}>
                  <FaClinicMedical />
                  <span>Modern Facilities</span>
                </div>
                <div className={styles.feature}>
                  <FaHospitalUser />
                  <span>Experienced Staff</span>
                </div>
                <div className={styles.feature}>
                  <FaHeartbeat />
                  <span>Comprehensive Services</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              className={styles.centerIntroImage}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
            >
              <div className={styles.imageWrapper}>
                <img
                  src="/assets/medical-center.jpg"
                  alt="Muigai Medical Center"
                />
                <div className={styles.imageGlow}></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Medical Center Stats */}
      <motion.section
        className={styles.statsSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        <div className="container">
          <div className={styles.statsGrid}>
            {centerStats.map((stat, idx) => (
              <motion.div
                key={idx}
                className={styles.statCard}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, delay: idx * 0.1 },
                  },
                }}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
              >
                <div className={styles.statIcon} style={{ color: stat.color }}>
                  <stat.icon />
                </div>
                <div className={styles.statNumber}>{stat.number}</div>
                <div className={styles.statLabel}>{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Doctor Introduction Section */}
      <section className={styles.doctorIntro}>
        <div className="container">
          <div className={styles.doctorIntroGrid}>
            <motion.div
              className={styles.doctorIntroImage}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <div className={styles.imageWrapper}>
                <img src="/assets/doctor.jpg" alt="Dr. Jack Kimeu" />
                <div className={styles.imageGlow}></div>
              </div>
            </motion.div>
            <motion.div
              className={styles.doctorIntroText}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
            >
              <h2>Meet Dr. Jack Nguthi Kimeu</h2>
              <p>
                Dr. Jack Nguthi Kimeu is a dynamic healthcare leader with over
                15 years of transformative experience. He is dedicated to
                enhancing employee wellness and optimizing healthcare delivery.
                With a Master's degree in Organizational Development, a
                Bachelor's in Counseling Psychology, and a Bachelor in Clinical
                Medicine & Surgery from Egerton University 2004 to 2006, he
                blends clinical expertise with strategic insight to drive
                impactful health initiatives.
              </p>
              <p>
                His innovative approach to health promotion programs has
                significantly boosted employee engagement and satisfaction,
                fostering a culture of wellness within organizations. Skilled in
                budget management and insurance processing, he has streamlined
                operations to maximize efficiency and resource allocation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Doctor Stats */}
      <motion.section
        className={styles.statsSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        <div className="container">
          <div className={styles.statsGrid}>
            {doctorStats.map((stat, idx) => (
              <motion.div
                key={idx}
                className={styles.statCard}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, delay: idx * 0.1 },
                  },
                }}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
              >
                <div className={styles.statIcon} style={{ color: stat.color }}>
                  <stat.icon />
                </div>
                <div className={styles.statNumber}>{stat.number}</div>
                <div className={styles.statLabel}>{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Doctor Cards */}
      <motion.section
        className={styles.cardsSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
      >
        <div className="container">
          <div className={styles.cardsGrid}>
            {doctorCards.map((card, idx) => (
              <motion.div
                key={idx}
                className={styles.card}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, delay: idx * 0.1 },
                  },
                }}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
              >
                <div className={styles.cardHeader}>
                  <div
                    className={styles.cardIcon}
                    style={{ backgroundColor: card.color }}
                  >
                    <card.icon />
                  </div>
                  <h3 className={styles.cardTitle}>{card.title}</h3>
                </div>
                <ul className={styles.cardList}>
                  {card.items.map((item, i) => (
                    <li key={i}>
                      <span className={styles.checkmark}>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Philosophy Section */}
      <section className={styles.philosophySection}>
        <div className="container">
          <motion.div
            className={styles.philosophyContent}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className={styles.philosophyIcon}>
              <FaHandHoldingHeart />
            </div>
            <h2>Our Philosophy</h2>
            <p>
              We believe that optimal healthcare goes beyond treating
              symptoms—it's about empowering individuals and organizations to
              achieve wellness. By blending clinical expertise with
              organizational development, we create environments where patients
              and staff thrive.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;
